/**
 * App bootstrap.
 *
 * Replaces `window.onload` from GlobalEvents.ts.
 * Registered via `window.addEventListener('load', bootstrapApp)` so it
 * runs AFTER all scripts have loaded (preserving timing of original onload).
 */

import { ornaVersionUpdatedNotification } from '@/services/version-service';
import { applyLegacyThemes } from '@/services/theme-service';
import { removeUpdateOverlay } from '@/utils/browser';
import { App } from '@/services/app-compat';
import moment from 'moment';

export function bootstrapApp() {
  if (_bootstrapped) return;
  _bootstrapped = true;

  if (!(window as any).isSupportedBrowser()) {
    document.getElementById("unsupportedBrowserDialog")!.style.display = "flex";
    applyLegacyThemes();
    return;
  }

  const w = window as any;
  App.setCurrentFrontendVersion(w.ORNA_VERSION);
  w.api.setCurrentBackendVersion(w.ORNA_BACKEND_VERSION);
  moment().format();

  // Check if logged in every 3 minutes
  App.isLoggedInInterval = setInterval(() => {
    App.isLoggedIn(undefined, true, 'interval');
  }, 180 * 1000);

  // Set iOS app based on user agent
  (window as any).setIOSApp((window as any).isIOSAppFromUserAgent());

  App.isUpdatingPanicButtonBlinkStateOnPageLoad = true;

  App.createRoutes();

  // Track officers location every 4s
  if (typeof (window as any).trackUser === 'function') {
    (window as any).trackUser();
  }
  App.trackLatLngInterval = setInterval(() => {
    if (typeof (window as any).trackUser === 'function') {
      (window as any).trackUser();
    }
  }, 4000);

  // Enter key handler for login
  document.addEventListener("keypress", function (e) {
    if (e.keyCode === 13) {
      const signIn = document.getElementById("btnSignIn") as HTMLElement;
      if (signIn && signIn.offsetWidth > 0) {
        signIn.click();
      }
    }
  });

  // Android resize handler
  if ((window as any).isAndroid()) {
    window.addEventListener("resize", function () {
      if (document.activeElement!.tagName == "INPUT" || document.activeElement!.tagName == "TEXTAREA") {
        window.setTimeout(function () {
          (document.activeElement as any).scrollIntoViewIfNeeded();
        }, 0);
      }
    });
  }


  // Theme CSS loading from localStorage.
  // The early bootstrap script in index.html may have already injected the
  // theme <link> as a render-blocking resource. Only create a new one if
  // it doesn't already exist.
  var themeColor = localStorage.getItem("OrnaThemeColor");
  if ((window as any).hasStringValue(themeColor)) {
    var themeFileMap: Record<string, string> = {
      'kendo.custom': '/Content/theme-blue-dark.css',
      'kendo.customGrey': '/Content/theme-gray-dark.css'
    };
    var themeHref = themeFileMap[themeColor!];
    if (themeHref) {
      const existingLink = document.querySelector('link[title=styles-animations]') as HTMLLinkElement;
      if (!existingLink) {
        const newLink = document.createElement('link');
        newLink.rel = 'stylesheet';
        newLink.href = themeHref;
        newLink.type = 'text/css';
        newLink.title = 'styles-animations';
        document.head.appendChild(newLink);
      }
    }
    document.querySelectorAll('.select-color').forEach((el: Element) => { (el as HTMLInputElement).checked = false; });
    const activeColorInput = document.querySelector("input[data-color='" + themeColor + "']") as HTMLInputElement;
    if (activeColorInput) activeColorInput.checked = true;
  }

  // Theme the statically-rendered elements in index.html (e.g. the
  // unsupported-browser dialog), which otherwise stay unthemed until a switch.
  applyLegacyThemes();

  // Remove early-bootstrap styles injected by index.html inline script
  // (no longer needed — RouteHandler manages SectionSignIn, overlay managed by removeUpdateOverlay)
  const earlyStyle = document.getElementById('early-bootstrap-style');
  if (earlyStyle) earlyStyle.remove();

  // Remove update overlay if present (persisted through reload via sessionStorage)
  removeUpdateOverlay();

  // Get app version from native app
  setTimeout(() => {
    (window as any).getAppVersionFromApp();
  }, 0);

  // Version updated notification
  ornaVersionUpdatedNotification();
}

// Guard flag — bootstrap() in main.tsx calls bootstrapApp() directly
// before mounting the router. The load listener is kept as a fallback.
let _bootstrapped = false;
window.addEventListener('load', () => {
  if (!_bootstrapped) bootstrapApp();
});
